import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { getVoteLabels } from '../data/decisionsData';
import { fetchMemberVoteHistory } from '../memberHistoryApi';
import { useLanguage } from '../i18n/language';
import './MemberHistoryPage.css';

export default function MemberHistoryPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const voteLabels = useMemo(() => getVoteLabels(isAr), [isAr]);

  const DECISION_STATUS = useMemo(() => ({
    pending: { label: isAr ? 'قيد التصويت' : 'Pending', cls: 'hist-card__outcome--pending' },
    approved: { label: isAr ? 'اعتُمد' : 'Approved', cls: 'hist-card__outcome--approved' },
    rejected: { label: isAr ? 'رُفض' : 'Rejected', cls: 'hist-card__outcome--rejected' },
  }), [isAr]);

  const VOTE_FILTER_TABS = useMemo(() => [
    { key: 'all', label: isAr ? 'الكل' : 'All', iconName: 'list' },
    { key: 'approve', label: voteLabels.approve.label, iconName: 'check-circle' },
    { key: 'reject', label: voteLabels.reject.label, iconName: 'x-circle' },
    { key: 'abstain', label: voteLabels.abstain.label, iconName: 'clock' },
  ], [isAr, voteLabels]);

  const t = {
    title: isAr ? 'سجل تصويتي' : 'My Voting History',
    subtitle: isAr ? 'تاريخ مشاركتك في قرارات الشركة' : 'Your participation history in branch decisions',
    totalVoted: isAr ? 'قرار صوّتت عليه' : 'voted decisions',
    approve: isAr ? 'موافق' : 'Approve',
    reject: isAr ? 'رافض' : 'Reject',
    abstain: isAr ? 'ممتنع' : 'Abstain',
    voteDist: isAr ? 'توزيع أصواتي' : 'My vote distribution',
    totalVotes: isAr ? 'صوت إجمالي' : 'total votes',
    search: isAr ? 'بحث في القرارات...' : 'Search decisions...',
    allResults: isAr ? 'كل النتائج' : 'All outcomes',
    matched: isAr ? 'نتيجة مطابقة' : 'matched results',
    clear: isAr ? 'مسح الفلاتر' : 'Clear filters',
    loading: isAr ? 'جارٍ تحميل سجل التصويت…' : 'Loading vote history...',
    noMatch: isAr ? 'لا توجد سجلات مطابقة' : 'No matching records',
    noMatchSub: isAr ? 'لم تصوّت على أي قرار بعد، أو جرّب تغيير الفلاتر' : 'You have not voted yet, or try changing filters',
    details: isAr ? 'تفاصيل القرار' : 'Decision details',
    loadFailed: isAr ? 'فشل التحميل' : 'Failed to load',
    votersProgress: isAr ? 'من' : 'of',
    votersVoted: isAr ? 'صوّتوا' : 'voted',
  };
  const navigate = useNavigate();

  const [voted, setVoted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [search, setSearch]           = useState('');
  const [filterVote, setFilterVote]   = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const rows = await fetchMemberVoteHistory();
        if (!cancelled) setVoted(rows);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t.loadFailed);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [t.loadFailed]);

  const filtered = useMemo(() => voted.filter((d) => {
    const matchSearch = !search || d.title.includes(search) || d.branch.includes(search);
    const matchVote   = filterVote === 'all'   || d.myVote === filterVote;
    const matchStatus = filterStatus === 'all' || d.status === filterStatus;
    return matchSearch && matchVote && matchStatus;
  }), [voted, search, filterVote, filterStatus]);

  const counts = {
    all:     voted.length,
    approve: voted.filter((d) => d.myVote === 'approve').length,
    reject:  voted.filter((d) => d.myVote === 'reject').length,
    abstain: voted.filter((d) => d.myVote === 'abstain').length,
  };

  const pct = (n) => voted.length ? Math.round((n / voted.length) * 100) : 0;

  return (
    <div className="hist-page">

      {/* ── Hero banner ── */}
      <div className="hist-hero">
        <div className="hist-hero__left">
          <div className="hist-hero__icon-wrap">
            <Icon name="report" size={28} color="#fff" strokeWidth={1.6} />
          </div>
          <div>
            <div className="hist-hero__title">{t.title}</div>
            <div className="hist-hero__sub">{t.subtitle}</div>
          </div>
        </div>

        <div className="hist-hero__chips">
          <div className="hist-hero__chip hist-hero__chip--total">
            <span className="hist-hero__chip-num">{counts.all}</span>
            <span className="hist-hero__chip-lbl">{t.totalVoted}</span>
          </div>
          <div className="hist-hero__chip hist-hero__chip--approve">
            <Icon name="check-circle" size={13} color="#02c3a6" />
            <span className="hist-hero__chip-num" style={{ color: '#02c3a6' }}>{counts.approve}</span>
            <span className="hist-hero__chip-lbl">{t.approve}</span>
          </div>
          <div className="hist-hero__chip hist-hero__chip--reject">
            <Icon name="x-circle" size={13} color="#c62828" />
            <span className="hist-hero__chip-num" style={{ color: '#c62828' }}>{counts.reject}</span>
            <span className="hist-hero__chip-lbl">{t.reject}</span>
          </div>
          <div className="hist-hero__chip hist-hero__chip--abstain">
            <Icon name="clock" size={13} color="#546e7a" />
            <span className="hist-hero__chip-num" style={{ color: '#546e7a' }}>{counts.abstain}</span>
            <span className="hist-hero__chip-lbl">{t.abstain}</span>
          </div>
        </div>
      </div>

      {/* ── Vote breakdown bar ── */}
      {voted.length > 0 && (
        <div className="hist-breakdown">
          <div className="hist-breakdown__label-row">
            <span className="hist-breakdown__heading">{t.voteDist}</span>
            <span className="hist-breakdown__total">{voted.length} {t.totalVotes}</span>
          </div>
          <div className="hist-breakdown__track">
            {counts.approve > 0 && (
              <div className="hist-breakdown__seg hist-breakdown__seg--approve" style={{ width: `${pct(counts.approve)}%` }}>
                {pct(counts.approve) >= 10 && <span>{pct(counts.approve)}%</span>}
              </div>
            )}
            {counts.reject > 0 && (
              <div className="hist-breakdown__seg hist-breakdown__seg--reject" style={{ width: `${pct(counts.reject)}%` }}>
                {pct(counts.reject) >= 10 && <span>{pct(counts.reject)}%</span>}
              </div>
            )}
            {counts.abstain > 0 && (
              <div className="hist-breakdown__seg hist-breakdown__seg--abstain" style={{ width: `${pct(counts.abstain)}%` }}>
                {pct(counts.abstain) >= 10 && <span>{pct(counts.abstain)}%</span>}
              </div>
            )}
          </div>
          <div className="hist-breakdown__legend">
            <span className="hist-breakdown__legend-item"><span className="hist-breakdown__dot hist-breakdown__dot--approve" />{voteLabels.approve.label} ({counts.approve})</span>
            <span className="hist-breakdown__legend-item"><span className="hist-breakdown__dot hist-breakdown__dot--reject" />{voteLabels.reject.label} ({counts.reject})</span>
            <span className="hist-breakdown__legend-item"><span className="hist-breakdown__dot hist-breakdown__dot--abstain" />{voteLabels.abstain.label} ({counts.abstain})</span>
          </div>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="hist-filters">
        {/* Vote type tabs */}
        <div className="hist-filters__tabs">
          {VOTE_FILTER_TABS.map((row) => (
            <button
              type="button"
              key={row.key}
              className={`hist-tab hist-tab--${row.key}${filterVote === row.key ? ' hist-tab--active' : ''}`}
              onClick={() => setFilterVote(row.key)}
            >
              <Icon name={row.iconName} size={13} />
              {row.label}
              <span className="hist-tab__count">{counts[row.key] ?? counts.all}</span>
            </button>
          ))}
        </div>

        {/* Search + status */}
        <div className="hist-filters__right">
          <div className="hist-search">
            <Icon name="search" size={13} color="#90a4ae" />
            <input
              className="hist-search__input"
              placeholder={t.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && <button className="hist-search__clear" onClick={() => setSearch('')}>✕</button>}
          </div>
          <select className="hist-filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">{t.allResults}</option>
            <option value="pending">{DECISION_STATUS.pending.label}</option>
            <option value="approved">{DECISION_STATUS.approved.label}</option>
            <option value="rejected">{DECISION_STATUS.rejected.label}</option>
          </select>
        </div>
      </div>

      {/* ── Results count ── */}
      {(search || filterVote !== 'all' || filterStatus !== 'all') && (
        <div className="hist-results-info">
          <Icon name="search" size={12} color="#90a4ae" />
          {filtered.length} {t.matched}
          <button className="hist-results-clear" onClick={() => { setSearch(''); setFilterVote('all'); setFilterStatus('all'); }}>
            {t.clear}
          </button>
        </div>
      )}

      {/* ── Timeline ── */}
      {loadError && (
        <div className="hist-results-info" style={{ color: '#c62828' }}>
          <Icon name="warning" size={12} color="#c62828" />
          {loadError}
        </div>
      )}
      {loading ? (
        <div className="hist-empty">
          <div className="hist-empty__sub">{t.loading}</div>
        </div>
      ) : (
      <>
      {filtered.length === 0 ? (
        <div className="hist-empty">
          <div className="hist-empty__icon">
            <Icon name="report" size={52} color="#cfd8dc" strokeWidth={1.2} />
          </div>
          <div className="hist-empty__title">{t.noMatch}</div>
          <div className="hist-empty__sub">{t.noMatchSub}</div>
        </div>
      ) : (
        <div className="hist-timeline">
          {filtered.map((d, i) => {
            const vl = voteLabels[d.myVote];
            const dst = DECISION_STATUS[d.status] || DECISION_STATUS.pending;
            return (
              <div
                key={d.id}
                className={`hist-card hist-card--${d.myVote}`}
                style={{ animationDelay: `${i * 0.06}s` }}
              >
                {/* Left accent + vote pill */}
                <div className="hist-card__side">
                  <div className={`hist-card__vote-pill hist-card__vote-pill--${d.myVote}`}>
                    <Icon name={vl.iconName} size={15} color="#fff" strokeWidth={2.2} />
                    <span>{vl.label}</span>
                  </div>
                  {i < filtered.length - 1 && <div className="hist-card__line" />}
                </div>

                {/* Content */}
                <div className="hist-card__content">
                  <div className="hist-card__header">
                    <div className="hist-card__meta">
                      <span className="hist-card__branch">
                        <Icon name="branch" size={11} color="#90a4ae" />
                        {d.branch}
                      </span>
                      <span className="hist-card__date">
                        <Icon name="calendar" size={11} color="#90a4ae" />
                        {d.myVotedAt || d.date}
                      </span>
                    </div>
                    <span className={`hist-card__outcome ${dst.cls}`}>{dst.label}</span>
                  </div>

                  <div className="hist-card__title">{d.title}</div>

                  {d.myNote && (
                    <div className="hist-card__note">
                      <Icon name="decisions" size={11} color="#90a4ae" />
                      <span>"{d.myNote}"</span>
                    </div>
                  )}

                  <div className="hist-card__footer">
                    <div className="hist-card__voters-info">
                      <Icon name="user" size={11} color="#b0bec5" />
                      <span>{d.votes.length} {t.votersProgress} {d.totalMembers} {t.votersVoted}</span>
                    </div>
                    <button
                      className="hist-card__btn"
                      onClick={() => navigate(`/member/decisions/${d.id}`)}
                    >
                      <Icon name="eye" size={13} color="#02c3a6" />
                      {t.details}
                    </button>
                  </div>
                </div>

                {/* Index badge */}
                <div className="hist-card__index">{i + 1}</div>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
}
